import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from './DataTable';
import { TicketVolumeChart } from './TicketVolumeChart';
import { Badge } from '@/components/ui/badge';

interface CatalogTaskMonitoringProps {
  catalogTasks: any[];
  showFullDetails?: boolean;
}
interface CatalogTask {
  Number: string;
  Priority: string;
  State: string;
  "Assigned to": string;
  "Short description": string;
  Opened: string;
  Duration: number;
  Impact: string;
  "Task type": string;
  "Due date": string;
  Service?: string;
  "Opened by": string;
}

interface TaskOriginTableProps {
  catalogTasks: CatalogTask[];
}

function TaskOriginTable({ catalogTasks }: TaskOriginTableProps) {
  // Define AMS team members
  const amsTeamMembers = [
    "Shivam Singh",
    "Rishabh Yadav", 
    "Chhavi Jain",
    "Shravan Singh",
    "Aman Singh"
  ];

  // Define End Users
  const endUsers = [
    "Sebastian Petriw",
    "Arial Montalvo", 
    "David Berber",
    "Veronica Jack",
    "Luis Cabello",
    "Yurii Kostenko",
    "Prasad Morye",
    "Jerwin Mascarinas",
    "Peter Ross",
    "Vaibhav Malhotra",
    "Daniel Cohen",
    "Mike Veldboer"
  ];

  // Group tasks by "Opened by"
  const taskOrigins = catalogTasks.reduce((acc: { [key: string]: CatalogTask[] }, task) => {
    const openedBy = task["Opened by"] || "Unknown";
    if (!acc[openedBy]) {
      acc[openedBy] = [];
    }
    acc[openedBy].push(task);
    return acc;
  }, {});

  // Separate by teams
  const endUserOrigins = Object.entries(taskOrigins)
    .filter(([openedBy]) => endUsers.includes(openedBy))
    .sort(([, a], [, b]) => b.length - a.length);

  const amsTeamOrigins = Object.entries(taskOrigins)
    .filter(([openedBy]) => amsTeamMembers.includes(openedBy))
    .sort(([, a], [, b]) => b.length - a.length);

  const integrationTeamOrigins = Object.entries(taskOrigins)
    .filter(([openedBy]) => !amsTeamMembers.includes(openedBy) && !endUsers.includes(openedBy))
    .sort(([, a], [, b]) => b.length - a.length);

  const renderTableRow = (openedBy: string, tasks: CatalogTask[]) => {
    const highPriority = tasks.filter(t => t.Priority === "2 - High").length;
    const moderatePriority = tasks.filter(t => t.Priority === "3 - Moderate").length;
    const normalPriority = tasks.filter(t => t.Priority === "4 – Normal").length;

    return (
      <tr key={openedBy} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{openedBy}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{tasks.length}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{highPriority}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moderatePriority}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{normalPriority}</td>
      </tr>
    );
  };

  const renderTotalRow = (title: string, allTasks: CatalogTask[]) => {
    const totalTickets = allTasks.length;
    const totalHighPriority = allTasks.filter(t => t.Priority === "2 - High").length;
    const totalModeratePriority = allTasks.filter(t => t.Priority === "3 - Moderate").length;
    const totalNormalPriority = allTasks.filter(t => t.Priority === "4 – Normal").length;

    return (
      <tr className="bg-gray-100 font-semibold border-t-2 border-gray-300">
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">Total {title}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalTickets}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalHighPriority}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalModeratePriority}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">{totalNormalPriority}</td>
      </tr>
    );
  };

  const renderSectionHeader = (title: string) => (
    <tr className="bg-blue-50">
      <td colSpan={5} className="px-6 py-3 text-left text-sm font-bold text-blue-900 uppercase tracking-wider">
        📊 {title}
      </td>
    </tr>
  );

  const renderTableHeader = () => (
    <tr className="bg-gray-50">
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Opened By</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Tickets</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">High Priority (P1 + P2)</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Moderate Priority (3)</th>
      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Normal Priority (4)</th>
    </tr>
  );

  // Calculate totals for each section
  const endUserTasks = endUserOrigins.flatMap(([, tasks]) => tasks);
  const amsTeamTasks = amsTeamOrigins.flatMap(([, tasks]) => tasks);
  const integrationTeamTasks = integrationTeamOrigins.flatMap(([, tasks]) => tasks);

  return (
    <div className="mt-8">
      <h3 className="text-xl font-semibold mb-4">Task Origin Analysis</h3>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <tbody className="bg-white divide-y divide-gray-200">
            {/* End User Section */}
            {endUserOrigins.length > 0 && (
              <>
                {renderSectionHeader("End User")}
                {renderTableHeader()}
                {endUserOrigins.map(([openedBy, tasks]) => renderTableRow(openedBy, tasks))}
                {renderTotalRow("", endUserTasks)}
              </>
            )}
            
            {/* Spacing between sections */}
            {(endUserOrigins.length > 0 && (amsTeamOrigins.length > 0 || integrationTeamOrigins.length > 0)) && (
              <tr className="bg-white">
                <td colSpan={5} className="py-2"></td>
              </tr>
            )}
            
            {/* AMS Team Section */}
            {amsTeamOrigins.length > 0 && (
              <>
                {renderSectionHeader("AMS Team")}
                {renderTableHeader()}
                {amsTeamOrigins.map(([openedBy, tasks]) => renderTableRow(openedBy, tasks))}
                {renderTotalRow("", amsTeamTasks)}
              </>
            )}

            {/* Spacing between sections */}
            {(amsTeamOrigins.length > 0 && integrationTeamOrigins.length > 0) && (
              <tr className="bg-white">
                <td colSpan={5} className="py-2"></td>
              </tr>
            )}
            
            {/* Integration Team Section */}
            {integrationTeamOrigins.length > 0 && (
              <>
                {renderSectionHeader("Integration Team")}
                {renderTableHeader()}
                {integrationTeamOrigins.map(([openedBy, tasks]) => renderTableRow(openedBy, tasks))}
                {renderTotalRow("", integrationTeamTasks)}
              </>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CatalogTaskMonitoring({ catalogTasks, showFullDetails = false }: CatalogTaskMonitoringProps) {
  // Calculate status counts
  const statusCounts = catalogTasks.reduce((acc: { [key: string]: number }, task) => {
    const status = task.state || task.status || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {});

  const columns = [
    {
      key: "number",
      header: "Task #",
      cell: (item: any) => item.number || item.number || "N/A",
    },
    {
      key: "description",
      header: "Description",
      cell: (item: any) => (
        <div className="max-w-xs truncate">
          {item.short_description || item.description || "No description"}
        </div>
      ),
    },
    {
      key: "priority",
      header: "Priority",
      cell: (item: any) => {
        const priority = (item.priority || "").toLowerCase();
        let badgeVariant: "default" | "secondary" | "destructive" | "outline" = "outline";
        
        if (priority === "critical" || priority === "1") {
          badgeVariant = "destructive";
        } else if (priority === "high" || priority === "2") {
          badgeVariant = "default";
        }
        
        return (
          <Badge variant={badgeVariant}>
            {item.priority || "Unknown"}
          </Badge>
        );
      },
    },
    {
      key: "created",
      header: "Created",
      cell: (item: any) => {
        const date = new Date(item.created || item.created_at || "");
        return isNaN(date.getTime())
          ? "Unknown"
          : date.toLocaleDateString() + " " + date.toLocaleTimeString();
      },
    },
    {
      key: "status",
      header: "Status",
      cell: (item: any) => (
        <Badge variant="outline">
          {item.state || item.status || "Unknown"}
        </Badge>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed Complete</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts['Closed Complete'] || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Closed Skipped</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts['Closed Skipped'] || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Open</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts['Open'] || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{statusCounts['Pending'] || 0}</div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <TicketVolumeChart 
          data={catalogTasks} 
          title="Catalog Task Volume Over Time" 
        />
        <TaskOriginTable catalogTasks={catalogTasks} />
        <Card>
          <CardHeader>
            <CardTitle>Catalog Tasks</CardTitle>
          </CardHeader>
          <CardContent>
            <DataTable 
              data={catalogTasks} 
              columns={columns} 
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
